// The activity grid's day buckets, ported from the iOS ActivityGrid: weekday
// rows, week columns, the newest week on the right, a month label where a
// column opens a month. Pure, so a test can hand it a day; the component
// turns the columns into rectangles.
import type { Millis } from '@shared/plan';

export const CELL = 13, GAP = 4, PITCH = CELL + GAP, LABEL_H = 16, ROWS = 7;
export const GRID_H = LABEL_H + ROWS * CELL + (ROWS - 1) * GAP;
/** A year of columns: more than any width shows, the oldest clipped off the left. */
export const WEEKS = 52;

export interface Column {
  weekStart: Millis;
  /** Set where this week opens a month the columns before it did not. */
  label?: string;
  /** One stamp per weekday row, or null for a day still to come. */
  days: (Millis | null)[];
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** Midnight at the start of the local day holding `t`. */
export const startOfDay = (t: Millis): Millis => new Date(t).setHours(0, 0, 0, 0);

/** `n` days on from a local midnight, landing on a midnight even across a clock change. */
export function addDays(day: Millis, n: number): Millis {
  const d = new Date(day);
  d.setDate(d.getDate() + n);
  return d.setHours(0, 0, 0, 0);
}

/**
 * `weeks` columns ending with the week that holds `today`. `firstWeekday`
 * numbers the week's first day as Date.getDay() does; Monday by default,
 * since the app speaks British English.
 */
export function activityColumns(today: Millis, weeks: number, firstWeekday = 1): Column[] {
  const day0 = startOfDay(today);
  const into = (new Date(day0).getDay() - firstWeekday + 7) % 7;
  const currentWeekStart = addDays(day0, -into);
  let lastMonth = -1;
  const columns: Column[] = [];
  for (let col = 0; col < weeks; col++) {
    const weekStart = addDays(currentWeekStart, -7 * (weeks - 1 - col));
    const column: Column = {
      weekStart,
      days: Array.from({ length: ROWS }, (_, row) => { const d = addDays(weekStart, row); return d > day0 ? null : d; }),
    };
    const month = new Date(weekStart).getMonth();
    if (month !== lastMonth) { lastMonth = month; column.label = MONTHS[month]; }
    columns.push(column);
  }
  return columns;
}

/** How deep a day is shaded for its count: 0 is the empty lattice, then four steps of blueprint. */
export function shade(count: number): 0 | 1 | 2 | 3 | 4 {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count <= 3) return 2;
  if (count <= 5) return 3;
  return 4;
}
